import { useCallback, useEffect, useRef, useState } from 'react'
import {
  api,
  clearToken,
  getToken,
  setToken,
  type Conversation,
  type Health,
} from './api'
import { telegramWebApp } from './telegram'
import { useChat } from './useChat'
import { useKeyboardInset } from './useKeyboard'
import { Composer } from './components/Composer'
import { Login } from './components/Login'
import { Message } from './components/Message'
import { Sidebar } from './components/Sidebar'
import { Tasks } from './components/Tasks'
import { anchorFrom, type Anchor } from './anchor'
import { useTasks } from './useTasks'
import { PanelLeft, Tasks as TasksIcon } from './components/icons'

type Stage = 'loading' | 'login' | 'ready'

function useTheme() {
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('ozarnik_theme')
    if (saved) return saved === 'dark'
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark)
    localStorage.setItem('ozarnik_theme', dark ? 'dark' : 'light')
  }, [dark])
  return { dark, toggle: () => setDark((v) => !v) }
}

export default function App() {
  const [stage, setStage] = useState<Stage>('loading')
  const [health, setHealth] = useState<Health | null>(null)
  const [userName, setUserName] = useState('')
  const [webEnabled, setWebEnabled] = useState(false)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeId, setActiveId] = useState<number | null>(null)
  const [models, setModels] = useState<string[]>([])
  const [currentModel, setCurrentModel] = useState<string | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [tasksOpen, setTasksOpen] = useState(false)
  const [tasksAnchor, setTasksAnchor] = useState<Anchor>({
    bottom: 8,
    left: 8,
    origin: 'bottom left',
  })
  // Remembered like the theme: re-collapsing the panel on every visit would
  // undo a choice the user already made.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('ozarnik_sidebar') === 'collapsed',
  )
  const { dark, toggle } = useTheme()
  const keyboard = useKeyboardInset()
  const typing = keyboard > 0

  const toggleCollapsed = useCallback(() => {
    setCollapsed((v) => {
      localStorage.setItem('ozarnik_sidebar', v ? 'open' : 'collapsed')
      return !v
    })
  }, [])

  const onConvCreated = useCallback((id: number) => {
    setActiveId((prev) => (prev === id ? prev : id))
  }, [])

  /**
   * Creates the conversation the first message will go into.
   *
   * Done on send rather than on open: an empty conversation per app launch
   * would litter the list, and this way opening and closing costs nothing.
   *
   * Deliberately does not switch to it — `useChat` does that once it has
   * claimed the conversation, so that switching cannot race the message being
   * typed into it. See the history effect there.
   */
  const ensureConversation = useCallback(async () => {
    const { id } = await api.createConversation()
    return id
  }, [])

  // `transportKind` is deliberately not read: which pipe carries the bytes is
  // plumbing, and the UI shows the user only what they can act on.
  const { messages, busy, reconnecting, send, stop } =
    useChat(activeId, onConvCreated, ensureConversation)

  const tasks = useTasks(tasksOpen)

  /**
   * Показывать ли вход в задачи.
   *
   * Агентский тред — всегда: там их заводят, и попасть в них надо до того,
   * как первая появится. Обычный — только пока что-то живо: задачи общие на
   * весь чат, поэтому запущенная из телеграма или из соседнего треда должна
   * оставаться достижимой, а значит и останавливаемой. Список из одних
   * законченных не показывается нигде: смотреть там не на что.
   */
  const activeMode =
    conversations.find((c) => c.id === activeId)?.mode ?? 'chat'
  const tasksVisible =
    activeMode === 'agent' || tasks.liveCount > 0 || tasks.approval !== null

  // Панель могла остаться открытой после того, как последняя задача кончилась
  // и кнопка исчезла, — тогда закрываем: держать поверхность, к которой уже
  // нет входа, нельзя.
  useEffect(() => {
    if (!tasksVisible && tasksOpen) setTasksOpen(false)
  }, [tasksVisible, tasksOpen])

  const bottom = useRef<HTMLDivElement>(null)
  const scroller = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)

  // Автопрокрутка только если пользователь и так внизу — иначе он не сможет
  // прочитать старое сообщение, пока идёт генерация.
  useEffect(() => {
    if (pinned.current) bottom.current?.scrollIntoView({ block: 'end' })
  }, [messages])

  const refreshConversations = useCallback(async () => {
    try {
      const list = await api.conversations()
      setConversations(list)
      // Deliberately does not restore the server's active conversation.
      // Opening the app starts a fresh chat and the old ones stay one tap
      // away; restoring it dropped you back into an old thread instead.
    } catch {
      // не критично: список подтянется при следующем действии
    }
  }, [])

  /**
   * Уборка при выходе из диалога: если в нём ничего не написано, сервер его
   * снесёт. Пусто или нет — решает сервер: клиент может не знать про
   * сообщение, долетевшее секунду назад.
   */
  const leaveConversation = useCallback(
    async (id: number | null) => {
      if (id === null) return
      try {
        const { removed } = await api.cleanupConversation(id)
        if (removed) await refreshConversations()
      } catch {
        // Не критично: пустышка подметётся при следующем выходе.
      }
    },
    [refreshConversations],
  )

  // Закрытие вкладки — тоже выход. Обработчик читает id из ref, иначе он
  // застрянет на том диалоге, который был активен при подписке.
  const activeIdRef = useRef<number | null>(null)
  useEffect(() => {
    activeIdRef.current = activeId
  }, [activeId])
  useEffect(() => {
    const onLeave = () => {
      const id = activeIdRef.current
      if (id !== null) void api.cleanupConversationOnUnload(id)
    }
    window.addEventListener('pagehide', onLeave)
    return () => window.removeEventListener('pagehide', onLeave)
  }, [])

  // A file picker can hide the Telegram webview. Keep the current conversation
  // when it becomes visible again; only the new-chat action starts a new one.

  const boot = useCallback(async () => {
    try {
      const h = await api.health()
      setHealth(h)

      // Внутри Telegram входим по подписанной initData: виджет там не
      // работает, и показывать его — тупик. Делаем это до /me, иначе первый
      // же 401 увёл бы на экран входа, которым внутри Telegram не
      // воспользоваться.
      const tg = telegramWebApp()
      if (tg) {
        // Mini Apps open at roughly half height; a chat needs the whole sheet.
        tg.expand?.()
      }

      const signIn = async () => {
        if (!tg) return
        tg.ready()
        const r = await api.authTelegramWebApp(tg.initData)
        setToken(r.token)
      }

      if (tg && !getToken()) await signIn()

      let me
      try {
        me = await api.me()
      } catch (e) {
        // A stored token that has gone bad — expired, or signed with a bot
        // token that has since been rotated — used to be a dead end: the
        // sign-in above only runs when there is NO token, so every reload
        // re-used the broken one and landed on a login screen that cannot
        // render a widget inside Telegram. Nothing short of clearing storage
        // got you out, which a webview user cannot do.
        //
        // initData is re-signed on every launch, so throwing the old token
        // away and asking again is always safe here.
        if (!tg) throw e
        clearToken()
        await signIn()
        me = await api.me()
      }

      setUserName(me.user_name)
      setWebEnabled(me.web_enabled)
      setStage('ready')
      await refreshConversations()
      const m = await api.models()
      setModels(m.models)
      setCurrentModel(m.current ?? m.default)
    } catch {
      setStage('login')
    }
  }, [refreshConversations])

  useEffect(() => {
    void boot()
  }, [boot])

  // Название диалога генерится на сервере в фоне — обновляем список,
  // когда ответ дописан.
  useEffect(() => {
    if (!busy && stage === 'ready') void refreshConversations()
  }, [busy, stage, refreshConversations])

  // Ход закончился — самое вероятное место, где задача только что завелась.
  // Дешевле, чем опрашивать вхолостую: в обычном треде это единственный
  // момент, когда кнопке есть смысл появиться.
  const reloadTasks = tasks.reload
  useEffect(() => {
    if (!busy && stage === 'ready') void reloadTasks()
  }, [busy, stage, reloadTasks])

  if (stage === 'loading') {
    return (
      <div className="flex h-full items-center justify-center text-sm text-(--ink-faint)">
        загружаю…
      </div>
    )
  }

  if (stage === 'login') {
    return (
      <Login
        botUsername={health?.bot_username ?? ''}
        onDone={() => {
          setStage('loading')
          void boot()
        }}
      />
    )
  }

  const empty = messages.length === 0

  return (
    <div className="app-shell flex h-full bg-(--ground)">
      <Sidebar
        conversations={conversations}
        activeId={activeId}
        open={sidebarOpen}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
        userName={userName}
        dark={dark}
        onToggleTheme={toggle}
        onLogout={
          health?.anonymous
            ? undefined
            : () => {
                clearToken()
                location.reload()
              }
        }
        tasksVisible={tasksVisible}
        tasksCount={tasks.liveCount}
        tasksOpen={tasksOpen}
        onToggleTasks={(e) => {
          setTasksAnchor(anchorFrom(e.currentTarget, 'above'))
          setTasksOpen((v) => !v)
        }}
        webEnabled={webEnabled}
        onToggleWeb={async (enabled) => {
          // Оптимистично, с откатом: переключатель, который «нажался», а на
          // сервере не поменялся, — это настройка, которая врёт.
          setWebEnabled(enabled)
          try {
            await api.setWeb(enabled)
          } catch {
            setWebEnabled(!enabled)
          }
        }}
        onToggleMode={async (id, mode) => {
          setConversations((prev) =>
            prev.map((c) => (c.id === id ? { ...c, mode } : c)),
          )
          await api.setConversationMode(id, mode).catch(() => {})
          await refreshConversations()
        }}
        onTogglePin={async (id, pinned) => {
          // Optimistic: the row jumps to its new section immediately, then we
          // reconcile with the server, which owns the ordering.
          setConversations((prev) =>
            prev.map((c) => (c.id === id ? { ...c, pinned } : c)),
          )
          await api.pinConversation(id, pinned).catch(() => {})
          await refreshConversations()
        }}
        onClose={() => setSidebarOpen(false)}
        onSelect={(id) => {
          const left = activeId
          setActiveId(id)
          setSidebarOpen(false)
          // Сначала переключились, потом подметаем покинутый: наоборот
          // список успел бы моргнуть удалением активного диалога.
          if (left !== id) void leaveConversation(left)
        }}
        onNew={async (mode) => {
          const left = activeId
          const { id } = await api.createConversation(mode)
          setActiveId(id)
          setSidebarOpen(false)
          if (left !== id) await leaveConversation(left)
          await refreshConversations()
        }}
        onRename={async (id, title) => {
          // Оптимистично: правим локально, потом синхронизируем.
          setConversations((prev) =>
            prev.map((c) => (c.id === id ? { ...c, title } : c)),
          )
          await api.renameConversation(id, title).catch(() => {})
          await refreshConversations()
        }}
        onDelete={async (id) => {
          await api.deleteConversation(id).catch(() => {})
          if (activeId === id) setActiveId(null)
          await refreshConversations()
        }}
      />

      {/*
        No top bar. Account, theme and logout moved into the sidebar footer and
        the model picker into the composer, which left the header holding
        nothing but a mobile menu button — so it became one, floating.
        On an empty conversation a growing spacer below the composer pulls it
        and the greeting into one centred group; sending the first message
        eases that spacer to zero, so the composer travels down to the bottom
        edge instead of snapping there. Animating `flex-grow` rather than
        toggling `justify-center` is what makes the move tweenable at all —
        `justify-content` has no interpolatable states.
      */}
      {/* Padding, not a transform: the message list has to shrink with it so
          it stays scrollable above the keyboard. Deliberately untransitioned —
          iOS streams visualViewport events through the keyboard's own
          animation, and a second easing on top of that reads as lag. */}
      <main
        className="flex min-w-0 flex-1 flex-col"
        style={{ paddingBottom: keyboard }}
      >
        <button
          type="button"
          onClick={() => setSidebarOpen((v) => !v)}
          className="fixed top-3 left-3 z-10 rounded-lg bg-(--surface)/80 p-2 text-(--ink-dim)
                     backdrop-blur transition hover:text-(--ink) md:hidden"
          aria-label="диалоги"
        >
          <PanelLeft size={22} />
        </button>

        {/* Тот же вход, что в подвале колонки, но на телефоне колонка — это
            закрытая шторка, и живая задача осталась бы не видна вовсе.
            Зеркалит кнопку меню напротив. */}
        {tasksVisible && (
          <button
            type="button"
            data-tasks-toggle
            onClick={(e) => {
              setTasksAnchor(anchorFrom(e.currentTarget, 'below'))
              setTasksOpen((v) => !v)
            }}
            title="Фоновые задачи"
            aria-label={
              tasks.liveCount > 0
                ? `фоновые задачи: ${tasks.liveCount}`
                : 'фоновые задачи'
            }
            className="fixed top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full
                       bg-(--surface)/80 px-2.5 py-2 text-(--ink-dim) backdrop-blur
                       transition hover:text-(--ink) md:hidden"
          >
            <TasksIcon size={16} />
            {tasks.liveCount > 0 && (
              <span className="text-xs leading-none text-(--ink)">
                {tasks.liveCount}
              </span>
            )}
          </button>
        )}


        {empty ? (
          <div
            key="body"
          className={`flex min-h-0 flex-1 flex-col justify-end px-4 pb-7 text-center
                        transition-opacity duration-300 ${
                          typing ? 'opacity-0' : 'opacity-100'
                        }`}
          >
            <h1 className="display welcome-enter mx-auto text-(--ink)">
              Что у вас сегодня на повестке?
            </h1>
          </div>
        ) : (
          <>
          <header className="flex h-16 shrink-0 items-center justify-center px-14 md:justify-start md:px-7">
            <span className="max-w-[min(70vw,48rem)] truncate text-sm font-medium text-(--ink-dim)">
              {conversations.find((c) => c.id === activeId)?.title || 'Новый чат'}
            </span>
          </header>
          <div
            key="body"
            ref={scroller}
            onScroll={(e) => {
              const el = e.currentTarget
              pinned.current =
                el.scrollHeight - el.scrollTop - el.clientHeight < 80
            }}
            className="min-h-0 flex-1 overflow-y-auto px-4 py-8"
          >
            <div className="mx-auto flex max-w-3xl flex-col gap-9 sm:gap-12">
              {messages.map((m) => (
                <Message key={m.id} message={m} />
              ))}
              <div ref={bottom} />
            </div>
          </div>
          </>
        )}

        <div key="bar" className="flex items-center justify-center gap-3">
          {reconnecting && (
            <span className="py-2 text-xs text-(--ink-faint)">
              связь потеряна, восстанавливаю…
            </span>
          )}
        </div>

        {/*
          Keyed so React keeps the same instance when the branch above swaps
          between the greeting and the message list — without it the composer
          would remount and drop whatever was typed or attached.
        */}
        <Composer
          key="composer"
          busy={busy}
          models={models}
          currentModel={currentModel}
          onModelChange={(model) => {
            setCurrentModel(model)
            void api.setModel(model).catch(() => {})
          }}
          onSend={send}
          onStop={stop}
        />

        {/* The counterweight. Grows to match the message area while the chat is
            empty, which centres the composer; eases to nothing once there is a
            conversation, carrying the composer down with it.

            It also collapses while the keyboard is up, so the composer sits
            straight on top of it rather than stranded mid-screen with a gap. */}
        <div
          aria-hidden
          className="shrink-0 transition-[flex-grow] duration-300 ease-out"
          style={{ flexGrow: empty && !typing ? 1 : 0, flexBasis: 0 }}
        />
      </main>

      {/* Задачи живут рядом с чатом, а не внутри него: они общие на весь чат,
          а не на диалог. Вход в них — в подвале колонки, где и остальные
          настройки уровня чата; раньше здесь висела кнопка `fixed bottom-3
          right-3`, и она просто закрывала собой кнопку отправки. */}
      <Tasks
        open={tasksOpen}
        anchor={tasksAnchor}
        onClose={() => setTasksOpen(false)}
        state={tasks}
      />
    </div>
  )
}
